'use client'

import Link from 'next/link'
import { useEffect, useId, useLayoutEffect, useRef, useState } from 'react'

import type { MaterialTier } from '@/lib/estimator/pricing/price-book'
import { Checkbox } from '@/components/ui/checkbox'
import { Icon } from '@/components/ui/icon'

import { TierChipRow } from './TierChipRow'

// Premium material *films*. Economy is deliberately NOT in this row: it is not a
// lesser material, it is a discounted BUILD of the same Standard film (installed
// from smaller pieces with seams + a few omissions). Listing it beside Specialty
// and Carbon read as "cheap material", which it is not — so it moves to the
// separate "Economy build" checkbox below, and picking it forces Standard film.
export const MATERIAL_TIER_OPTIONS: ReadonlyArray<{ key: MaterialTier; label: string; hint?: string }> = [
  { key: 'standard', label: 'Standard' },
  { key: 'specialty', label: 'Specialty', hint: '+25%' },
  { key: 'carbon', label: 'Carbon Fiber', hint: '+50%' },
]

export const MATERIAL_TIER_DEFAULT: MaterialTier = 'standard'

// Dedicated explainer for the Economy build (seams, exclusions, film limits).
const ECONOMY_LEARN_MORE_HREF = '/economy-wrap'

interface MaterialTierPickerProps {
  value: MaterialTier
  onChange: (tier: MaterialTier) => void
  // Economy is never offered at Quarter coverage (amendment 5.1). When false the
  // Economy build checkbox is hidden and the material row shows every film.
  allowEconomy?: boolean
}

export function MaterialTierPicker({ value, onChange, allowEconomy = true }: MaterialTierPickerProps) {
  // A stale economy pick can outlive a switch to Quarter coverage for one render
  // before the parent resets it; treat it as Standard so the UI never shows a
  // disallowed economy state.
  const isEconomy = allowEconomy && value === 'economy'

  return (
    <div className="flex flex-col gap-2">
      {allowEconomy && (
        <div className="flex items-center gap-1.5">
          <Checkbox
            isSelected={isEconomy}
            onChange={(selected) => onChange(selected ? 'economy' : MATERIAL_TIER_DEFAULT)}
          >
            <span className="text-sm">Economy build</span>
            <span className="ml-1 text-xs text-muted">-30%</span>
          </Checkbox>
          <EconomyInfo />
        </div>
      )}

      {isEconomy ? (
        // Economy build is same-as-Standard film, so material is not a choice here.
        <div role="group" aria-label="Material" className="flex flex-wrap items-center gap-2">
          <span className="w-full text-sm text-muted">Material</span>
          <span className="rounded-full border border-accent bg-accent/10 px-3 py-1 text-sm text-foreground">
            Standard
          </span>
          <span className="text-xs text-muted">Economy uses Standard film</span>
        </div>
      ) : (
        <TierChipRow<MaterialTier>
          label="Material"
          options={MATERIAL_TIER_OPTIONS}
          value={value === 'economy' ? MATERIAL_TIER_DEFAULT : value}
          onChange={(tier) => onChange(tier)}
        />
      )}
    </div>
  )
}

// Click-to-toggle info tip. Native button + panel (not HeroUI Tooltip, which is
// hover-oriented and has no project wrapper); click-toggle works on touch too.
function EconomyInfo() {
  const [open, setOpen] = useState(false)
  const [offsetLeft, setOffsetLeft] = useState<number | null>(null)
  const ref = useRef<HTMLDivElement>(null)
  const panelRef = useRef<HTMLDivElement>(null)
  const panelId = useId()

  useEffect(() => {
    if (!open) return
    const onDocPointer = (e: MouseEvent | TouchEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false)
    }
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setOpen(false)
    }
    document.addEventListener('mousedown', onDocPointer)
    document.addEventListener('keydown', onKey)
    return () => {
      document.removeEventListener('mousedown', onDocPointer)
      document.removeEventListener('keydown', onKey)
    }
  }, [open])

  // The static right-0 anchor assumed the icon "sits well right of content",
  // which only held on the wide layouts it was first built against. On a
  // narrow phone with the icon near the left of the row (e.g. /get-a-quote),
  // a right-anchored w-64 panel overflows past the left edge of the viewport
  // and its opening text renders off-screen. Measure and clamp instead.
  useLayoutEffect(() => {
    if (!open) return
    const reposition = () => {
      const anchor = ref.current
      const panel = panelRef.current
      if (!anchor || !panel) return
      const anchorRect = anchor.getBoundingClientRect()
      const margin = 16
      const desiredLeft = anchorRect.right - panel.offsetWidth
      const maxLeft = window.innerWidth - panel.offsetWidth - margin
      const clampedLeft = Math.min(Math.max(desiredLeft, margin), maxLeft)
      setOffsetLeft(clampedLeft - anchorRect.left)
    }
    reposition()
    window.addEventListener('resize', reposition)
    window.addEventListener('scroll', reposition, { passive: true, capture: true })
    return () => {
      window.removeEventListener('resize', reposition)
      window.removeEventListener('scroll', reposition, { capture: true })
    }
  }, [open])

  return (
    <div ref={ref} className="relative inline-flex">
      <button
        type="button"
        aria-label="What is Economy build?"
        aria-expanded={open}
        aria-controls={panelId}
        onClick={() => setOpen((o) => !o)}
        className="flex h-5 w-5 items-center justify-center rounded-full text-muted hover:text-foreground"
      >
        <Icon icon="question-circle" className="text-base" aria-hidden="true" />
      </button>
      {open && (
        <div
          ref={panelRef}
          id={panelId}
          role="tooltip"
          // right-0 is the pre-measurement default (first paint, before the
          // layout effect above runs); the inline `left`/`right` style then
          // wins and overrides it once the clamped position is known.
          className="absolute right-0 top-full z-50 mt-2 w-64 max-w-[calc(100vw-2rem)] rounded-lg border border-border bg-surface p-3 text-xs leading-relaxed text-muted shadow-lg"
          style={offsetLeft === null ? undefined : { left: offsetLeft, right: 'auto' }}
        >
          Installed from smaller pieces with some seams, no door handles, and some other
          small exclusions.{' '}
          {/* Ink + underline, not `text-accent`: this picker is shared by /book
              (v2 since phase 2 task 5) and /dashboard/quotes (still v1). Under
              the v3 teal pairing rule (teal fill carries white text; teal as a
              plain text/link colour on a white panel is unverified against the
              new palette), ink + underline stays the safe choice and reads as
              a link in both scopes. */}
          <Link href={ECONOMY_LEARN_MORE_HREF} className="text-foreground underline">
            Learn more
          </Link>
          .
        </div>
      )}
    </div>
  )
}
